<template>
  <EditorPanel class="pb-data-panel">
    <template #header>
      <ObjectHeader />
    </template>

    <template v-if="mode">
      <ReportMessage
        :item="mode"
        :default-state="true"
        :children-state="false"
      >
        {{ translate('pb2.modePropertiesView.modeReportTitle') }}
      </ReportMessage>
      <ItemProperties class="pb-mode-properties">
        <h3>{{ translate('pb2.modePropertiesView.modePropertiesTitle') }}</h3>
        <InputWithLoader
          :label="translate('pb2.modePropertiesView.modeNameLabel')"
          :placeholder="modeNamePlaceholder"
          :value="mode.data.name"
          :disabled="readonly"
          class="pb-half-margin-top"
          @input="set('name', $event)"
        />
      </ItemProperties>
      <ButtonGroup
        column
        class="pb-mode-actions"
      >
        <ButtonWithLoader
          :disabled="readonly"
          @click="duplicateMode"
        >
          <span class="ellipsis">{{ translate('pb2.modePropertiesView.duplicateModeLabel') }}</span>
        </ButtonWithLoader>
        <ButtonWithLoader
          v-if="canBeDeleted"
          class="btn-error"
          :disabled="readonly"
          @click="openConfirm"
        >
          <span class="ellipsis">{{ translate('pb2.modePropertiesView.deleteModeLabel') }}</span>
        </ButtonWithLoader>
      </ButtonGroup>
      <DeleteModeConfirmDialog
        v-model="deleteModeConfirm"
        :mode="mode"
      />
    </template>
  </EditorPanel>
</template>

<script setup>
import { computed, ref } from 'vue'

import routeNames from '@/router/names'

import useModeStore from '@/stores/mode.store'

import useDeleteHotkey from '@/composables/useDeleteHotkey'
import { useCurrent, useReadonly } from '@/composables/useProfile'
import useTranslate from '@/composables/useTranslate'

import InputWithLoader from '@/components/InputWithLoader.vue'
import ButtonWithLoader from '@/components/ButtonWithLoader.vue'
import EditorPanel from '@/components/EditorPanel/EditorPanel.vue'
import ReportMessage from '@/components/ReportMessage/ReportMessage.vue'
import ObjectHeader from '@/components/ObjectHeader/ObjectHeader.vue'
import ButtonGroup from '@/components/ButtonGroup/ButtonGroup.vue'
import ItemProperties from '@/components/ItemProperties/ItemProperties.vue'
import DeleteModeConfirmDialog from '@/components/DeleteModeConfirmDialog/DeleteModeConfirmDialog.vue'

const modeStore = useModeStore()

const { translate } = useTranslate()

const set = (key, value) => modeStore.edit({ [key]: value })

function duplicateMode() {
  return modeStore.duplicate()
}

const {
  modes,
  mode,
} = useCurrent()
const readonly = useReadonly()

const modeNamePlaceholder = computed(() => (
  mode.value?.defaultName || translate('pb2.modePropertiesView.modeNamePlaceholder')
))

const deleteModeConfirm = ref(false)
function openConfirm() {
  deleteModeConfirm.value = true
}

const canBeDeleted = computed(() => (
  mode.value
  && modes.value.length > 1
))

const deleteHotkeyOnRoutes = [
  routeNames.Mode,
]

const deleteHotkeyEnabled = computed(() => !deleteModeConfirm.value)
useDeleteHotkey(deleteHotkeyOnRoutes, deleteHotkeyEnabled, () => {
  if (!readonly.value && canBeDeleted.value) {
    openConfirm()
  }
})
</script>

<style lang="scss">
@use '@nicolaudie-group/ng-ui-kit/defines/_fonts.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_colors.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_config.define.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_flexbox.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_generic.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_responsive.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_theme.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_animations.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_transitions.mixin.scss';

@use '/src/styles/_pb.scss';

.pb-mode-actions {
  @include flexbox.flex-grow(1);
  @include flexbox.justify-content(flex-end);
  padding-top: pb.$spacing;
}
</style>
